/**
 * File Name          : firmwareupdate.component.ts
 * Author             : Jared Laine Dyreson
 * Version            : 0.1.0
 * Date               : 2026-03-18
 * Description        : Short Description (manual edit required)
 *
 * Copyright (C) Innovative Design Labs, Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * This file is subject to the terms and conditions defined in
 * file 'IDL_PROJECTTECH_LICENSE.txt', which is part of this source code package.
 */

import { Component, ChangeDetectorRef } from '@angular/core';
import { RWUpdateServerService } from '../../services/rwupdate-server.service';
import { DxLoadIndicatorComponent } from "../../../../node_modules/devextreme-angular/ui/load-indicator/index";
import { CommonModule } from '@angular/common';
import { DxButtonModule, DxPopupModule, DxProgressBarModule } from 'devextreme-angular';
import { DatasnapDeviceService } from '../../services/datasnap-device.service';
import { ActivatedRoute, Router } from '@angular/router';
import { DatasnapDevice } from '../../models/datasnap-device.model';
import { Device_State, OTA_DIAG } from '../../../../projects/idl-physens/src/lib/models/physens-devices/physens-device.model';
import { ConsoleCaptureService } from '../../services/console-capture.service';
import { isChargingState } from '../../../../projects/idl-physens/src/lib/models/battery_state_char.model';

const CHARGER_STOP_MESSAGE = 'Firmware update stopped: device placed on charger';

// Dev-only confirm-path test switch. When true, runUpdateFlow() skips every "already current /
// already staged / roll back" short-circuit and always uploads the target image, so the full
// cmdUpload -> cmdImageTest -> reboot -> (post-reboot) cmdImageConfirm path runs even when the
// target already sits in a slot. MUST stay false for shipping builds.
const FORCE_UPLOAD_FOR_CONFIRM_TEST = false;

// This error's only remaining use is waitForReconnectEvent()'s hard-timeout branch (still
// called, with hardTimeoutMs=0, by the rollback-recovery path, where that branch can never
// actually fire). Kept solely because that call site still constructs it.
class ReconnectToVerifyError extends Error {
  constructor() {
    super('Reconnect to verify');
    this.name = 'ReconnectToVerifyError';
  }
}

@Component({
  selector: 'app-firmwareupdate',
  standalone: true,
  imports: [CommonModule, DxProgressBarModule, DxButtonModule, DxPopupModule],
  templateUrl: './firmwareupdate.component.html',
  styleUrl: './firmwareupdate.component.scss'
})
export class FirmwareupdateComponent {

  uploadProgress = 0;
  // Synthetic progress override used once the upload itself is done and we move into the
  // reset/reboot/reconnect/verify state machine below. There is no real byte-progress
  // signal during that phase (mcumgr's uploadPercentage doesn't move), so this is purely
  // cosmetic — it must NEVER be the thing that decides when "Update Complete" shows.
  // That decision is made exclusively by rebootAfterConfirm() after a real post-reconnect,
  // read-only imageState check shows the active image is the target version AND confirmed.
  // See otaPhase below for the actual state machine.
  syntheticProgress: number | null = null;
  // Eased display value for the real upload (mcumgr.uploadPercentage is raw byte-offset
  // math — it can sit still for several seconds while a chunk retries over BLE, then the
  // final ack jumps straight from e.g. 91% to 100% in one tick). Binding the progress bar
  // directly to that raw value reads as "stuck, then teleports to done". This value chases
  // uploadPercentage at a capped rate instead, so the same jump plays out as a smooth ~2s
  // ramp rather than an instant snap.
  displayedUploadProgress = 0;
  private uploadProgressAnimTimer: ReturnType<typeof setInterval> | null = null;
  private syntheticProgressRampTimer: ReturnType<typeof setInterval> | null = null;
  device: DatasnapDevice | null | undefined = null;
  errorCount = 0;
  reconnecting = false;
  showManualReconnect = false;
  // Tracks an in-flight reselectDevice() call so the button can be disabled —
  // without this the user has no feedback while the chooser is open and keeps
  // clicking, and a second click while Chrome's native chooser is still on
  // screen throws SecurityError (gesture rejected), which looked like the
  // button "does nothing".
  reselecting = false;
  private cancelled = false;
  // Snapshot of device.deviceName taken before the reset is sent. During RECONNECTING
  // the underlying PhysensBleDevice handle can be mid-swap (manual reselect fetches a
  // fresh one), so device?.deviceName can transiently read undefined/stale right when
  // the fallback "Reconnect" button needs to show a name - read this snapshot instead
  // so the button is never blank or wrong purely due to that swap, independent of
  // whatever name Chrome's own native chooser does or doesn't display.
  rememberedDeviceName: string | undefined;

  // deviceId captured just before the OTA reset, so after a manual reconnect we can log whether
  // the device came back under a new address/id across the reboot (ChromeOS can re-grant a
  // different handle) — purely diagnostic.
  private preRebootDeviceId: string | undefined;

  // OTA state machine phase, purely for logging/diagnostics and as a single-flight guard
  // against duplicate reconnect/progress events while REBOOTING/RECONNECTING/VERIFYING —
  // every wait helper below settles exactly once and tears down its own subscription, so
  // a duplicate BLE event arriving after a phase has already resolved is a no-op.
  // 'installed' is the deliberately-ambiguous outcome: reset was sent and the image was
  // confirmed permanent pre-reboot, but no reconnect was observed within the timeout, so we
  // can neither prove success nor failure — never shown as either.
  // 'manualSelectRequired' is the next step once 'installed' would otherwise be a dead end: the
  // automatic post-reboot reconnect window elapsed, so the user must tap a native button that
  // calls requestDevice() directly (a real user gesture — never a timer) to find the device.
  private otaPhase: 'idle' | 'uploading' | 'resetting' | 'rebooting' | 'reconnecting' | 'verifying' | 'installed' | 'manualSelectRequired' | 'success' | 'failed' = 'idle';

  state: {
    unitId: string;
    levelId: string;
    lessonId: string;
  } = {
    unitId: '',
    levelId: '',
    lessonId: ''
  };

  primary_message = "Uploading Firmware - Please Wait";
  secondary_messages = [
    'Keep DataSnap close by',
    'Do not place on charger',
    'Do not close this window'
  ];
  update_complete = false;
  update_failed = false;
  // Set when the reboot/reconnect outcome is genuinely ambiguous (reset sent + pre-reboot
  // confirm succeeded, but no reconnect observed within the timeout) — NOT success, NOT
  // failure. Only gates the "Okay" exit button; never implies "up to date".
  update_ambiguous = false;
  // Set once the automatic post-reboot reconnect window (quiet period + raw-GATT poll loop)
  // elapses without reaching the device — gates the native "Tap to finish" button. Still NOT
  // success/failure: the device is presumed rebooted with the image confirmed permanent
  // pre-reset, but unproven until the user's tap drives a real reconnect + read-only verify.
  update_tap_required = false;
  // True while a tap-to-finish requestDevice()/gatt.connect() attempt is in flight, so the
  // button can show "Connecting..." and ignore a double-tap without ever disabling itself via
  // a timer (only the attempt's own settle re-enables it).
  tapToFinishBusy = false;
  tapToFinishMessage: string | null = null;
  // Gates the mid-upload stuck-reconnect screen: fired once by PhysensBleDevice's
  // otaReconnectStuck (forwarded through DatasnapDevice) after its own 2-strikes teardown has
  // already torn down the wedged connect state — this is purely UI, no auto-resume/retry of
  // its own. Deliberately separate from showSelectDevicePopup()'s popup, which stays hidden for
  // the whole OTA via !currentlyUpdating.
  update_reconnect_stuck = false;
  // True while finishManualTapReconnect() is in flight for the stuck-reconnect screen above.
  otaReconnectBusy = false;
  otaReconnectMessage: string | null = null;
  // Set once the stuck-reconnect tap succeeds — shows a standalone "Retry update" button
  // (retryUpdate(), which re-runs the pre-flight imageState check) rather than auto-resuming
  // the upload.
  update_reconnect_ready = false;
  // Set once the device's new active image version is known (the confirmed/uploaded target —
  // declared immediately on reset-ack, not read back post-reboot) so the completion log reflects
  // the actual target rather than a hard-coded string.
  private lastVerifiedVersion: string | undefined;
  // Stashed so tapToFinishReconnect() (triggered later by a user click, not by
  // rebootAfterConfirm's own call stack) still knows what version it's verifying against.
  private pendingOtaTargetVersion: string | undefined;
  // Same as above, for the hash — verifyAndConfirmActiveVersion() must confirm the exact
  // image that was test-armed pre-reboot, not just any active image with a matching version
  // string (two different builds could share one, and MCUboot's auto-revert-on-bad-boot can
  // otherwise read as a false "success" if the old confirmed image happens to match by version).
  private pendingOtaTargetHash: ArrayBuffer | undefined;

  // Single gated DIAG logger — all OTA diagnostic lines in this component route through here so
  // the OTA_DIAG master switch (shared with the BLE layer) mutes them in one place.
  private diag(message: string) {
    if (OTA_DIAG) console.log(`[FWUpdate][DIAG] ${message}`);
  }

  constructor(private activatedRoute: ActivatedRoute, private datasnapDeviceService: DatasnapDeviceService, private rwUpdateServerService: RWUpdateServerService, private router: Router, private cdr: ChangeDetectorRef, private consoleCaptureService: ConsoleCaptureService) {

  }

  ngOnInit() {
      // Access the deepest child route to get all params
      let child = this.activatedRoute;
      while (child) {
        if (child.firstChild) {
          child = child.firstChild;
        } else {
          this.state = history.state;

          let deviceId = this.activatedRoute.snapshot.paramMap.get('deviceId');
          if(deviceId) {
            this.device = this.datasnapDeviceService.devices.find(d => d.deviceId === deviceId);


            if(this.device) {
              this.device.onRemoveSubject.subscribe(()=>{
                this.device?.onRemoveSubject.unsubscribe();
                this.router.navigate(['home']);
              });
              this.device.otaReconnectStuck.subscribe(()=>{
                this.diag('upload-reconnect: stuck-reconnect screen shown');
                this.update_reconnect_stuck = true;
                this.update_reconnect_ready = false;
                this.otaReconnectMessage = null;
                this.cdr.detectChanges();
              });
              this.startUpdate()


            } else {
              console.error("Device with ID " + deviceId + " not found for firmware update");
              if(this.state.unitId && this.state.levelId && this.state.lessonId) {
                this.router.navigate(['home', this.state.unitId, this.state.levelId, this.state.lessonId]);
              } else {
                this.router.navigate(['home']);
              }
            }
          } else {
            console.error("No device ID provided for firmware update");
            if(this.state.unitId && this.state.levelId && this.state.lessonId) {
              this.router.navigate(['home', this.state.unitId, this.state.levelId, this.state.lessonId]);
            } else {
              this.router.navigate(['home']);
            }
          }

          break;
        }
      }
  }

  ngOnDestroy() {
    this.cancelled = true;
    this.stopUploadProgressAnimation();
    this.stopSyntheticProgressRamp();
    if(this.device) {
      this.device.currentlyUpdating = false;
    }
  }

  private startUploadProgressAnimation() {
    this.displayedUploadProgress = 0;
    this.stopUploadProgressAnimation();
    // 2%/tick @ 80ms = 25%/sec cap — fast enough that catching up to a burst of real
    // chunk acks (or the 91%->100% completion jump) reads as a quick ramp rather than a
    // multi-second crawl, but slow enough that it never looks instantaneous.
    this.uploadProgressAnimTimer = setInterval(() => {
      const target = this.device?.mcumgr?.uploadPercentage ?? 0;
      if(this.displayedUploadProgress < target) {
        this.displayedUploadProgress = Math.min(target, this.displayedUploadProgress + 2);
        this.cdr.detectChanges();
      } else if(this.displayedUploadProgress > target) {
        // Real percentage only moves forward, but guard against a fresh cmdUpload
        // restarting it at 0 (retry) so the bar doesn't hang at the old high value.
        this.displayedUploadProgress = target;
        this.cdr.detectChanges();
      }
    }, 80);
  }

  private stopUploadProgressAnimation() {
    if(this.uploadProgressAnimTimer) {
      clearInterval(this.uploadProgressAnimTimer);
      this.uploadProgressAnimTimer = null;
    }
  }

  private stopSyntheticProgressRamp() {
    if(this.syntheticProgressRampTimer) {
      clearInterval(this.syntheticProgressRampTimer);
      this.syntheticProgressRampTimer = null;
    }
  }

  // Cosmetic only: crawls syntheticProgress asymptotically toward (but never reaching) a
  // cap while REBOOTING/RECONNECTING/VERIFYING are in progress, so the bar never looks
  // frozen no matter how long the real reconnect takes. It is capped below 100 for its
  // entire run — nothing in this function can ever show "done". The ONLY place
  // syntheticProgress is set to 100 is finishProgressToComplete(), called exclusively after
  // a real post-reconnect, read-only verify (or, on the rollback-recovery path, a real
  // post-reboot imageState read) has confirmed the device landed on the target, confirmed
  // image. Time constant is the same on every platform — this function has no platform
  // branch, so the bar behaves identically on Windows and Chrome regardless of how long
  // their underlying reconnect paths actually take. progressCap/crawlCap default to the
  // rollback-recovery path's original values; rebootAfterConfirm() passes a higher cap since
  // it now covers a longer wait (reset -> disconnect -> silent reconnect, up to ~90s).
  private startRebootProgressCrawl(progressCap = 95, crawlCap = 96) {
    this.stopSyntheticProgressRamp();
    const startedAt = Date.now();
    const timeConstantMs = 11000;
    let capReachedAt: number | null = null;
    this.syntheticProgressRampTimer = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const fraction = 1 - Math.exp(-elapsed / timeConstantMs);
      const asymptotic = Math.round(progressCap * fraction);
      if(asymptotic < progressCap) {
        this.syntheticProgress = asymptotic;
      } else {
        if(capReachedAt === null) capReachedAt = elapsed;
        this.syntheticProgress = Math.min(crawlCap, progressCap + Math.floor((elapsed - capReachedAt) / 600));
      }
      this.cdr.detectChanges();
    }, 250);
  }

  // Strictly monotonic — never drops, unlike startRebootProgressCrawl's asymptotic curve.
  // Rises linearly from wherever syntheticProgress already is up to `cap` over `durationMs`,
  // then holds at `cap` for as long as the caller keeps the timer running past that point.
  // Used for the ~65s post-disconnect REBOOTING window, where no connect attempt of any kind
  // happens and the UI must still visibly progress the entire time without a button.
  private startLinearProgressRamp(durationMs: number, cap = 99) {
    this.stopSyntheticProgressRamp();
    const startedAt = Date.now();
    const floor = this.syntheticProgress ?? 0;
    this.syntheticProgressRampTimer = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const fraction = Math.min(1, elapsed / durationMs);
      const next = Math.round(floor + (cap - floor) * fraction);
      this.syntheticProgress = Math.max(this.syntheticProgress ?? 0, next);
      this.cdr.detectChanges();
    }, 250);
  }

  // Steps syntheticProgress up to 100 over a few ticks (rather than an instant jump) once
  // the pre-reboot confirm has already succeeded (or, on the rollback-recovery path, once a
  // real post-reboot imageState read has confirmed the device landed on an already-current
  // image) — this is purely a visual flourish on top of a result that has already been proven
  // real.
  private finishProgressToComplete(): Promise<void> {
    this.stopSyntheticProgressRamp();
    return new Promise((resolve) => {
      this.syntheticProgressRampTimer = setInterval(() => {
        const current = this.syntheticProgress ?? 100;
        if(current >= 100) {
          this.stopSyntheticProgressRamp();
          resolve();
          return;
        }
        this.syntheticProgress = Math.min(100, current + 2);
        this.cdr.detectChanges();
      }, 80);
    });
  }

  // Runs fn (expected to resolve/reject promptly while the device is still connected —
  // i.e. BEFORE a reset is sent) with a timeout, retrying once on either a thrown error or
  // a timeout, per the required flow: "Await each response; if a response is an error or
  // times out, retry once, then go to FAILED."
  private async sendWithRetry<T>(label: string, fn: () => Promise<T>, timeoutMs = 8000): Promise<T> {
    let lastError: unknown;
    for(let attempt = 0; attempt < 2; attempt++) {
      try {
        const result = await Promise.race([
          fn(),
          new Promise<never>((_, reject) => setTimeout(() => reject(new Error(`${label} timeout after ${timeoutMs}ms`)), timeoutMs))
        ]);
        if(attempt > 0) console.log(`[FWUpdate] ${label} succeeded on retry`);
        return result;
      } catch (e) {
        lastError = e;
        console.warn(`[FWUpdate] ${label} failed (attempt ${attempt + 1}/2):`, e);
      }
    }
    throw new Error(`${label} failed: ${(lastError as any)?.message ?? lastError}`);
  }

  // Sends the reset command. Unlike cmdImageTest/cmdImageConfirm (sent while still
  // connected, where a failure/timeout is a real problem worth retrying immediately),
  // cmdReset's own response races the reboot it triggers: if the device reboots before
  // acking, the pending response is force-rejected the moment the disconnect is observed
  // (PhysensBleDevice.updateDeviceState -> mcumgr.clearPendingResolvers). That rejection
  // is NOT a failure — it is direct evidence the reset worked. The actual proof the reset
  // "took" is owned entirely by the next step (waitForDisconnectEvent), which is what the
  // required flow's retry-on-no-disconnect logic is for. So this is deliberately
  // best-effort and never throws on its own.
  private async sendResetCommand(): Promise<void> {
    try {
      await this.device?.mcumgr?.cmdReset();
      console.log('[FWUpdate] state=RESETTING: reset command acknowledged by device');
    } catch (e) {
      console.log('[FWUpdate] state=RESETTING: reset command did not ack before disconnect (expected if the device rebooted immediately):', e);
    }
  }

  // Waits up to timeoutMs for a DISCONNECTED transition on deviceStateChangeSubject —
  // the hard evidence step 3 of the required flow calls for ("a GATT disconnect event
  // within ~15s of the reset command"). deviceStateChangeSubject is a BehaviorSubject, so
  // subscribing replays the CURRENT state first; that replay is deliberately ignored
  // (skippedReplay) since at the moment this is called the device is still connected and
  // we only care about the transition that happens AFTER the reset was sent.
  private waitForDisconnectEvent(timeoutMs: number): Promise<number | null> {
    return new Promise((resolve) => {
      const subject = this.device?.deviceStateChangeSubject;
      if(!subject) { resolve(null); return; }
      let settled = false;
      let skippedReplay = false;
      const timer = setTimeout(() => {
        if(settled) return;
        settled = true;
        sub.unsubscribe();
        resolve(null);
      }, timeoutMs);
      const sub = subject.subscribe((state) => {
        if(!skippedReplay) { skippedReplay = true; return; }
        if(settled) return;
        if(state === Device_State.DISCONNECTED) {
          settled = true;
          clearTimeout(timer);
          sub.unsubscribe();
          resolve(Date.now());
        }
      });
    });
  }

  // Waits for the device to come back to READY_FOR_PACKETS after a confirmed disconnect.
  // Reconnection itself (getDevices()+watchAdvertisements(), falling back to a plain
  // connect, all against a FRESH device handle rather than the pre-reboot one) is owned
  // entirely by PhysensBleDevice's scheduleReconnect()/tryWatchAdvertisementReconnect() —
  // unchanged by this state machine and identical on every platform. This only waits on
  // the result. A timeout here is explicitly NOT a failure per the required flow ("Timeout
  // is not failure, it is 'needs reconnect'") — it surfaces the manual Reconnect button
  // and keeps waiting; the automatic loop keeps retrying in the background regardless.
  private waitForReconnectEvent(softTimeoutMs = 120000, hardTimeoutMs = 0): Promise<void> {
    return new Promise((resolve, reject) => {
      const subject = this.device?.deviceStateChangeSubject;
      if(!subject) {
        reject(new Error("Device state subject unavailable"));
        return;
      }
      let settled = false;
      let hardTimer: ReturnType<typeof setTimeout> | null = null;
      const cleanup = () => {
        clearTimeout(softTimer);
        if(hardTimer) clearTimeout(hardTimer);
        sub.unsubscribe();
        removeSub.unsubscribe();
        staleAddressSub.unsubscribe();
      };
      const offerManualReconnectNow = (reason: string) => {
        if(settled || this.cancelled) return;
        console.warn(`[FWUpdate] state=RECONNECTING: ${reason} — offering manual reconnect`);
        this.reconnecting = true;
        this.showManualReconnect = true;
        this.cdr.detectChanges();
      };
      // As in the pre-existing reboot wait: don't pop the manual reconnect button on a
      // short timer — give the automatic watchAdvertisements()/backoff loop real runway
      // (it reliably recovers within ~30-70s in the field) before treating it as stuck.
      const manualReconnectGraceMs = 45000;
      const waitStartedAt = Date.now();
      const staleAddressSub = this.device!.staleAddressReconnectFailureSubject.subscribe(() => {
        if(settled || this.showManualReconnect) return;
        const elapsedMs = Date.now() - waitStartedAt;
        if(elapsedMs >= manualReconnectGraceMs) {
          offerManualReconnectNow(`reconnect still failing ${Math.round(elapsedMs / 1000)}s into reconnect wait`);
        }
      });
      const sub = subject.subscribe((state) => {
        if(settled) return;
        if(state === Device_State.READY_FOR_PACKETS) {
          settled = true;
          this.reconnecting = false;
          this.showManualReconnect = false;
          cleanup();
          console.log(`[FWUpdate] state=RECONNECTING: reconnected, method=${this.device?.reconnectMethod ?? 'unknown'}`);
          resolve();
          // The state change driving this came from a BLE/GATT event, not a DOM event
          // Angular is guaranteed to pick up for CD — force a render now.
          this.cdr.detectChanges();
        }
      });
      const removeSub = this.device!.onRemoveSubject.subscribe(() => {
        if(settled) return;
        settled = true;
        cleanup();
        reject(new Error("Device was removed while waiting for reconnect"));
      });
      const softTimer = setTimeout(() => {
        offerManualReconnectNow(`reconnect wait exceeded ${softTimeoutMs}ms — automatic reconnect still pending`);
      }, softTimeoutMs);
      // Hard cap (opt-in via hardTimeoutMs > 0): unlike the soft timer, this actually settles
      // the wait so a caller that must read device state afterward (verify) can never hang on a
      // device that never comes back. Rejects with ReconnectToVerifyError so the caller surfaces
      // the "Reconnect to Verify" state rather than declaring success or a hard failure.
      if(hardTimeoutMs > 0) {
        hardTimer = setTimeout(() => {
          if(settled) return;
          settled = true;
          cleanup();
          console.warn(`[FWUpdate] state=RECONNECTING: hard timeout after ${hardTimeoutMs}ms — device not back, cannot verify`);
          reject(new ReconnectToVerifyError());
        }, hardTimeoutMs);
      }
      if(this.cancelled) {
        settled = true;
        cleanup();
        reject(new Error("Firmware update cancelled"));
      }
    });
  }

  // After a device reboot the READY_FOR_PACKETS state can be published before mcumgr's
  // own notification handler is registered. An mcumgr write in that window silently drops
  // its reply and its Promise never settles. Race each attempt against a per-call timeout
  // so we can retry instead of hanging the whole verify step.
  private async mcumgrImageStateWithRetry(maxAttempts = 6, perAttemptTimeoutMs = 2000): Promise<{images: any[], splitstatus: number} | undefined> {
    for(let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const result = await Promise.race([
          this.device?.mcumgr?.cmdImageState(),
          new Promise<never>((_, reject) => setTimeout(() => reject(new Error('cmdImageState timeout')), perAttemptTimeoutMs))
        ]);
        if(result && (result as any).images) return result as any;
      } catch (e) {
        console.warn('[FWUpdate] cmdImageState attempt', attempt + 1, 'failed:', e);
      }
      await new Promise(resolve => setTimeout(resolve, 400 * (attempt + 1)));
    }
    throw new Error('cmdImageState failed after retries');
  }

  // Arms a one-shot TEST boot (pending, NOT permanent) for the image BEFORE the reset is sent,
  // by hash, via the required flow's "await each response; retry once on error/timeout, then
  // FAILED" rule (sendWithRetry). Deliberately does NOT call cmdImageConfirm here: if the new
  // image fails to boot cleanly, MCUboot auto-reverts to the previous known-good image on its
  // next reset since nothing was ever marked permanent. The image is only made permanent after
  // a real post-reboot verify proves the device is actually running the target version — see
  // verifyAndConfirmActiveVersion(), called from rebootAfterConfirm()/tapToFinishReconnect()
  // after reconnect. Logs the hash and the image state read before/after the test (OTA_DIAG) so
  // a field failure shows exactly what MCUboot reported. Throws (no reset sent) if the test
  // errors/times out after retry, or if it doesn't stick.
  private async testImageBeforeReboot(hash: ArrayBuffer, label: string): Promise<void> {
    this.diag(`${label}: test-arming hash=${this.bufferToHex(hash)}`);
    const preImageState = await this.device?.mcumgr?.cmdImageState();
    this.diag(`${label}: pre-test imageState=${JSON.stringify(preImageState?.images?.map((i: any) => ({slot: i.slot, version: i.version, active: i.active, confirmed: i.confirmed, pending: i.pending, permanent: i.permanent})))}`);

    await this.sendWithRetry(label, () => this.device!.mcumgr!.cmdImageTest(hash));

    const postImageState = await this.device?.mcumgr?.cmdImageState();
    this.diag(`${label}: post-test imageState=${JSON.stringify(postImageState?.images?.map((i: any) => ({slot: i.slot, version: i.version, active: i.active, confirmed: i.confirmed, pending: i.pending, permanent: i.permanent})))}`);
    const testedImage = postImageState?.images?.find((i: any) => this.bufferToHex(i.hash) === this.bufferToHex(hash));
    // The image isn't active until after the reboot that's about to happen, so "active" isn't
    // the right flag to check yet — pending is MCUboot's pre-reboot proof the one-shot test
    // swap is armed. It must stay NOT permanent here, or a bad boot could never revert.
    if(!testedImage?.pending) {
      console.error(`[FWUpdate] ${label}: FAILED - image not pending after test (state=${JSON.stringify(testedImage)})`);
      throw new Error("Firmware test did not stick before reboot");
    }
    console.log(`[FWUpdate] ${label}: test-armed before reboot (pending=${testedImage.pending} permanent=${testedImage.permanent})`);
  }

  // Compares two MCUboot/Zephyr version strings for equality via parseVersion(), tolerant of
  // the "0.5.4+0" / "0.5.4.0" / "v0.5.4" format differences.
  private versionsMatch(a: string | undefined, b: string | undefined): boolean {
    const pa = this.parseVersion(a);
    const pb = this.parseVersion(b);
    if(!pa || !pb) return false;
    return pa.versionMajor === pb.versionMajor && pa.versionMinor === pb.versionMinor
      && pa.versionBug === pb.versionBug && pa.versionSpecial === pb.versionSpecial;
  }

  // Reads imageState post-reconnect and compares the active image to the target. The active
  // image after a test-boot (testImageBeforeReboot) is only pending, not yet permanent — if the
  // version matches and it isn't confirmed yet, this sends cmdImageConfirm by hash NOW (the
  // device already proved it can boot the new image, which is exactly what the test-boot was
  // for), with the same "await, retry once, then FAILED" rule as the pre-reboot test. If the
  // version doesn't match, or confirm fails/doesn't stick, returns false so the caller fails the
  // update — an un-confirmed test image safely auto-reverts to the previous firmware on its own
  // next reset, same as a genuinely bad image would.
  private async verifyAndConfirmActiveVersion(targetVersionRaw: string | undefined, targetHash: ArrayBuffer | undefined): Promise<boolean> {
    const imageState = await this.mcumgrImageStateWithRetry().catch(() => undefined);
    const active = imageState?.images.find((i: any) => i.active) ?? imageState?.images?.[0];
    this.diag(`post-reconnect verify: active=${JSON.stringify(active ? {version: active.version, hash: this.bufferToHex(active.hash), confirmed: active.confirmed} : undefined)} target=${targetVersionRaw} targetHash=${this.bufferToHex(targetHash)}`);
    this.diag(`post-reconnect verify: all slots=${JSON.stringify(imageState?.images?.map((i: any) => ({slot: i.slot, version: i.version, hash: this.bufferToHex(i.hash), active: i.active, confirmed: i.confirmed, pending: i.pending, permanent: i.permanent})))}`);
    if(!active || !targetVersionRaw || !targetHash || !this.versionsMatch(active.version, targetVersionRaw)) return false;
    // Version string alone isn't proof of identity (two builds can share one) — require the
    // active image's hash to match the exact image that was test-armed pre-reboot, so a
    // MCUboot auto-revert back to the old (already-confirmed) image can never be misread as
    // this update having succeeded.
    if(this.bufferToHex(active.hash) !== this.bufferToHex(targetHash)) return false;
    if(active.confirmed) return true;

    try {
      await this.sendWithRetry('cmdImageConfirm (post-reboot)', () => this.device!.mcumgr!.cmdImageConfirm(active.hash));
    } catch (e) {
      console.error('[FWUpdate] post-reconnect confirm failed after retry', e);
      return false;
    }
    const postConfirmState = await this.mcumgrImageStateWithRetry().catch(() => undefined);
    const postConfirmActive = postConfirmState?.images.find((i: any) => i.active) ?? postConfirmState?.images?.[0];
    this.diag(`post-reconnect verify: post-confirm active=${JSON.stringify(postConfirmActive ? {version: postConfirmActive.version, confirmed: postConfirmActive.confirmed} : undefined)}`);
    return !!postConfirmActive?.confirmed && this.versionsMatch(postConfirmActive.version, targetVersionRaw);
  }

  // Bug 20264: the 1s charger poll is paused for the whole reset -> reboot -> verify window
  // (expectingReboot), and on ChromeOS tap-to-finish that window can last minutes. Read the
  // charge state explicitly before each step in it that writes to the device. Publishing the
  // reply lets DatasnapDeviceService abort + disconnect; the explicit call below is a fallback.
  private async stoppedForCharger(): Promise<boolean> {
    const state = await this.device?.readChargeStateForOta();
    if(state == null || !isChargingState(state)) return false;
    if(this.device?.currentlyUpdating) this.datasnapDeviceService.onChargerDetected(this.device);
    return true;
  }

  // Sends the reset, then requires REAL proof before declaring success: a confirmed disconnect
  // (reset resent up to 2 more times if no disconnect is observed — the reset ack itself races
  // the reboot and is not reliable proof on its own), then a reconnect (fully automatic — a
  // 45s quiet period while the device boots, then a raw-GATT poll loop; see
  // PhysensBleDevice.attemptPostRebootReconnect), then an imageState check that the active
  // image is the target version, confirming it permanent only once that's proven true (the
  // image was only test-armed pre-reboot by testImageBeforeReboot, so a bad boot can still
  // auto-revert — see verifyAndConfirmActiveVersion):
  //  - reconnect + match (+ confirm succeeds) -> SUCCESS ("Update Complete - Success!")
  //  - reconnect + version mismatch           -> FAILURE ("Update did not apply")
  //  - no automatic reconnect                 -> hands off to the native "Tap to finish" button
  //    (tapToFinishReconnect) rather than declaring success or failure; the device model's own
  //    connect handler also re-runs a read-only version of this same check the next time this
  //    device successfully connects from any session, as a safety net.
  private async rebootAfterConfirm(targetVersionRaw: string | undefined, targetHash: ArrayBuffer | undefined): Promise<'success' | 'installed' | 'manualSelectRequired'> {
    if(await this.stoppedForCharger()) throw new Error(CHARGER_STOP_MESSAGE);
    this.rememberedDeviceName = this.device?.deviceName;
    this.preRebootDeviceId = this.device?.deviceId;
    this.pendingOtaTargetVersion = targetVersionRaw;
    this.pendingOtaTargetHash = targetHash;
    // Narrower than currentlyUpdating: only true for this reset->reboot->reconnect window, so
    // the BLE layer's reconnect-retry safety nets stay exempted only here, not for the whole
    // OTA flow. Stays true through the tap-to-finish fallback too (cleared in both the
    // automatic-success path below and in tapToFinishReconnect()), so the automatic background
    // reconnect loop never races the manual one.
    this.device!.rebootInProgress = true;
    // Stashed so the device model's own connect handler can run a read-only version of this
    // check later even if this component instance never sees the reconnect (route torn down,
    // manual-select fallback, etc.) — that fallback never confirms, only compares and updates
    // the badge.
    this.device?.setPendingOtaVerifyTarget(targetVersionRaw);

    this.otaPhase = 'resetting';
    this.primary_message = "Restarting DataSnap...";
    this.secondary_messages = [
      'Keep DataSnap close by',
      'Do not place on charger',
      'Do not close this window'
    ];
    this.startRebootProgressCrawl(98, 99);
    this.cdr.detectChanges();
    console.log(`[FWUpdate] state=RESETTING: sending reset command t=${Date.now()}`);
    await this.sendResetCommand();

    let disconnectAt: number | null = null;
    for(let attempt = 0; attempt < 3 && disconnectAt === null; attempt++) {
      if(attempt > 0) {
        console.log(`[FWUpdate] state=RESETTING: no disconnect observed yet, resending reset (attempt ${attempt + 1}/3)`);
        await this.sendResetCommand();
      }
      disconnectAt = await this.waitForDisconnectEvent(15000);
    }
    console.log(`[FWUpdate] state=RESETTING: ${disconnectAt !== null ? `disconnect observed t=${disconnectAt}` : 'disconnect NOT observed after 3 reset attempts'}`);

    this.otaPhase = 'rebooting';
    this.primary_message = "Restarting DataSnap...";
    console.log(`[FWUpdate] state=REBOOTING: t=${Date.now()}`);
    // No BleClient.connect() / raw gatt.connect() happens for the first ~65s of this —
    // attemptPostRebootReconnect() itself owns that quiet period (see its own comment). The
    // progress bar rises monotonically the whole time so it never looks frozen or drops back.
    this.startLinearProgressRamp(65000, 99);

    const reconnected = await (this.device?.attemptPostRebootReconnect() ?? Promise.resolve(false));

    if(!reconnected) {
      this.stopSyntheticProgressRamp();
      this.otaPhase = 'manualSelectRequired';
      this.primary_message = "Your DataSnap restarted. Tap to finish.";
      this.secondary_messages = [];
      this.update_tap_required = true;
      console.log(`[FWUpdate] state=MANUAL_SELECT_REQUIRED: no automatic reconnect within the post-reboot window, target=${targetVersionRaw} — awaiting tap-to-finish`);
      this.cdr.detectChanges();
      return 'manualSelectRequired';
    }

    if(await this.stoppedForCharger()) {
      this.stopSyntheticProgressRamp();
      throw new Error(CHARGER_STOP_MESSAGE);
    }
    this.device!.rebootInProgress = false;
    // Narrower than rebootInProgress: the reconnect it guarded has already happened, but a
    // disconnect during this verify (+ post-boot confirm) window must still be excluded from
    // the mid-upload stuck-reconnect path (see PhysensDevice.otaVerifying).
    this.device!.otaVerifying = true;
    this.otaPhase = 'verifying';
    this.primary_message = "Verifying Update...";
    this.cdr.detectChanges();
    let verified: boolean;
    try {
      verified = await this.verifyAndConfirmActiveVersion(targetVersionRaw, targetHash);
    } finally {
      // try/finally: guarantees otaVerifying can never be left stuck true if
      // verifyAndConfirmActiveVersion() ever throws in the future.
      this.device!.otaVerifying = false;
    }
    if(!verified) {
      console.error(`[FWUpdate] state=VERIFYING: FAILED - active image after reconnect does not match confirmed target ${targetVersionRaw}`);
      throw new Error("Update did not apply");
    }

    this.lastVerifiedVersion = targetVersionRaw;
    await this.finishProgressToComplete();
    this.otaPhase = 'success';
    this.primary_message = "Update Complete - Success!";
    this.secondary_messages = ["Your DataSnap is now up to date!"];
    this.update_complete = true;
    if(this.device) {
      this.device.isFWOutOfDate = false;
      this.device.updateFailed = false;
      this.device.updateChecked = true;
      // Let keepalive/housekeeping resume now rather than waiting for the user to tap Okay —
      // SUCCESS is a terminal outcome, there's nothing left for updateInProgress to protect.
      this.device.currentlyUpdating = false;
    }
    console.log(`[FWUpdate] state=SUCCESS: t=${Date.now()} (reconnected, active image verified == ${targetVersionRaw} and confirmed)`);
    // Fire-and-forget housekeeping refresh — don't await. sendPacket hangs (no rejection)
    // when the link drops during the post-swap reboot cycle.
    this.device?.checkFWUpdate().catch(() => undefined);
    return 'success';
  }

  // Driven by the native "Tap to finish" button (template-gated on update_tap_required) shown
  // after attemptPostRebootReconnect()'s automatic window elapses without success — the only
  // remaining route on a platform without navigator.bluetooth.getDevices (ChromeOS). The actual
  // requestDevice() call happens synchronously inside PhysensBleDevice.finishManualTapReconnect
  // (no preceding await anywhere in this call chain, so the user gesture is preserved), which
  // also owns the BleClient.connect() retry loop. Never auto-retries on its own and never shows
  // a hard error or a fake success — a failed attempt just re-arms the same button.
  tapToFinishReconnect = () => {
    if(this.tapToFinishBusy) return;
    const deviceName = this.rememberedDeviceName;
    if(!this.device || !deviceName) {
      console.warn('[FWUpdate] tap-to-finish: missing device/name, cannot proceed');
      this.tapToFinishMessage = "Can't search for your DataSnap — please reload.";
      return;
    }
    this.tapToFinishBusy = true;
    this.tapToFinishMessage = null;
    this.cdr.detectChanges();

    this.device.finishManualTapReconnect().then(async (reconnected: boolean) => {
      if(!reconnected) {
        console.log('[FWUpdate] tap-to-finish: all attempts failed');
        this.tapToFinishMessage = "Wait a few seconds and tap again";
        this.tapToFinishBusy = false;
        this.cdr.detectChanges();
        return;
      }

      if(await this.stoppedForCharger()) {
        // Already aborted + disconnected; the onRemove subscription navigates away.
        this.tapToFinishBusy = false;
        this.update_tap_required = false;
        this.cdr.detectChanges();
        return;
      }
      this.otaPhase = 'verifying';
      this.device!.otaVerifying = true;
      this.primary_message = "Verifying Update...";
      this.secondary_messages = [];
      this.cdr.detectChanges();
      const targetVersionRaw = this.pendingOtaTargetVersion;
      const targetHash = this.pendingOtaTargetHash;
      let verified: boolean;
      try {
        verified = await this.verifyAndConfirmActiveVersion(targetVersionRaw, targetHash).catch(() => false);
      } finally {
        // try/finally: guarantees rebootInProgress/otaVerifying can never be left
        // stuck true if verifyAndConfirmActiveVersion() ever throws in the future.
        this.device!.rebootInProgress = false;
        this.device!.otaVerifying = false;
      }
      if(!verified) {
        console.error(`[FWUpdate] state=VERIFYING (tap-to-finish): FAILED - active image does not match confirmed target ${targetVersionRaw}`);
        this.tapToFinishBusy = false;
        this.handleUpdateFailure(new Error("Update did not apply"));
        this.cdr.detectChanges();
        return;
      }

      this.lastVerifiedVersion = targetVersionRaw;
      await this.finishProgressToComplete();
      this.otaPhase = 'success';
      this.primary_message = "Update Complete - Success!";
      this.secondary_messages = ["Your DataSnap is now up to date!"];
      this.update_complete = true;
      this.update_tap_required = false;
      this.tapToFinishBusy = false;
      if(this.device) {
        this.device.isFWOutOfDate = false;
        this.device.updateFailed = false;
        this.device.updateChecked = true;
        this.device.currentlyUpdating = false;
      }
      console.log(`[FWUpdate] state=SUCCESS (tap-to-finish): t=${Date.now()}`);
      this.device?.checkFWUpdate().catch(() => undefined);
      this.cdr.detectChanges();
    }).catch((e: any) => {
      console.warn('[FWUpdate] tap-to-finish: finishManualTapReconnect errored', e);
      this.tapToFinishMessage = "Wait a few seconds and tap again";
      this.tapToFinishBusy = false;
      this.cdr.detectChanges();
    });
  }

  // Driven by the native "Connection lost. Tap to reconnect" button (template-gated on
  // update_reconnect_stuck), shown after PhysensBleDevice's mid-upload 2-strikes teardown has
  // already run (BleClient.disconnect + clear writePromise + mcumgr.clearPendingResolvers +
  // queue reset) and fired otaReconnectStuck. The requestDevice() call happens synchronously
  // inside finishManualTapReconnect (no preceding await), preserving the user gesture. On
  // success this does NOT resume the upload; it only reveals "Retry update" (retryUpdate(),
  // which re-runs the pre-flight imageState check from scratch).
  uploadReconnectTap = () => {
    if(this.otaReconnectBusy) return;
    const deviceName = this.rememberedDeviceName;
    if(!this.device || !deviceName) {
      console.warn('[FWUpdate] upload-reconnect: missing device/name, cannot proceed');
      this.otaReconnectMessage = "Can't search for your DataSnap — please reload.";
      return;
    }
    this.otaReconnectBusy = true;
    this.otaReconnectMessage = null;
    this.diag('upload-reconnect: chooser opened');
    this.cdr.detectChanges();

    this.device.finishManualTapReconnect().then((reconnected: boolean) => {
      this.diag(`upload-reconnect: connect result = ${reconnected}`);
      this.otaReconnectBusy = false;
      if(reconnected) {
        this.update_reconnect_stuck = false;
        this.update_reconnect_ready = true;
      } else {
        this.otaReconnectMessage = "Wait a few seconds and tap again";
      }
      this.cdr.detectChanges();
    }).catch((e: any) => {
      console.warn('[FWUpdate] upload-reconnect: finishManualTapReconnect errored', e);
      this.otaReconnectBusy = false;
      this.otaReconnectMessage = "Wait a few seconds and tap again";
      this.cdr.detectChanges();
    });
  }

  downloadOtaLogs = () => {
    this.consoleCaptureService.downloadLogs();
  }

  // The actual update flow (pre-flight check, upload-or-reuse-staged-image, then
  // rebootAfterConfirm). Split out of startUpdate() so a re-entrant component instance
  // (see startUpdate below) can `await` this exact same run via device.fwUpdateInFlight
  // instead of racing it with a second, independent one.
  private async runUpdateFlow(): Promise<'success' | 'installed' | 'manualSelectRequired'> {
    if (FORCE_UPLOAD_FOR_CONFIRM_TEST) {
      console.warn('[FWUpdate] FORCE_UPLOAD_FOR_CONFIRM_TEST is ON — skipping already-current/staged/rollback short-circuits and forcing a full upload so cmdImageTest + post-reboot cmdImageConfirm run');
    }
    this.primary_message = "Uploading Firmware - Please Wait";
    this.secondary_messages = [
      'Keep DataSnap close by',
      'Do not place on charger',
      'Do not close this window'
    ];

    let imageState = await this.device?.mcumgr?.cmdImageState();
    console.log('[FWUpdate] pre-flight imageState:', JSON.stringify(imageState?.images?.map(i => ({slot: i.slot, version: i.version, confirmed: i.confirmed, active: i.active, pending: i.pending}))));
    if((imageState?.images.length??0) > 0) {
      // Gate the whole update flow on the device's actual ACTIVE slot version,
      // not just on why we got navigated here. Slot 0 is not always the active
      // one after a swap — trust image.active. Without this, once the device
      // is already current we re-flash it (0.5.4 -> 0.5.4).
      const activeImage = imageState!.images.find(i => i.active) ?? imageState!.images[0];
      const currentVersion = this.parseVersion(activeImage.version);
      console.log('[FWUpdate] activeImage.version raw:', activeImage.version, 'parsed:', currentVersion);
      if(currentVersion) {
        const outOfDate = await this.rwUpdateServerService.isFWOutOfDate(currentVersion);
        console.log('[FWUpdate] isFWOutOfDate:', outOfDate);
        if(!outOfDate && !FORCE_UPLOAD_FOR_CONFIRM_TEST) {
          // This branch is only reachable on a genuinely fresh run (device.fwUpdateInFlight
          // re-entry is handled entirely in startUpdate(), before runUpdateFlow() is ever
          // called) — so there is no reboot in flight here and it is safe to complete
          // instantly, matching the active image's confirmed, already-current state.
          this.primary_message = "Update Complete - Success!";
          this.secondary_messages = ["Your DataSnap is already up to date!"];
          this.update_complete = true;
          if(this.device) {
            this.device.isFWOutOfDate = false;
            this.device.updateFailed = false;
            this.device.updateChecked = true;
            this.device.currentlyUpdating = false;
          }
          return 'success';
        }
      }

      // Stuck-swap state: target already sits in a non-active slot from a prior
      // attempt whose reboot never took. mcumgr img_mgmt on the device REFUSES
      // writes to a confirmed slot — attempting cmdUpload here just crashes
      // the BLE link on the first chunk (verified in field logs: repeated
      // "upload percentage 0" followed by Disconnected). The only way forward
      // is cmdReset to let MCUboot boot the staged image, then verify.
      let stagedImage: any = null;
      for(const img of imageState!.images) {
        if(img.active) continue;
        const v = this.parseVersion(img.version);
        if(!v) continue;
        if(!(await this.rwUpdateServerService.isFWOutOfDate(v))) {
          stagedImage = img;
          break;
        }
      }
      if(stagedImage && !FORCE_UPLOAD_FOR_CONFIRM_TEST) {
        console.log('[FWUpdate] target already staged in slot', stagedImage.slot, '- resetting to activate (no upload possible over confirmed slot)');
        // Test-arm the staged image (pending, not permanent) before rebooting, while the BLE
        // link is still up. If the test fails or errors (after the one retry inside
        // testImageBeforeReboot), this throws and the update fails outright — no reset is sent,
        // so the device is left exactly as it was. Permanent confirm only happens after reboot,
        // once verifyAndConfirmActiveVersion() proves the device is really running this image.
        if(!stagedImage.confirmed) {
          await this.testImageBeforeReboot(stagedImage.hash, 'cmdImageTest (staged image)');
        }
        this.primary_message = "Applying Firmware Update - Please Wait";
        this.secondary_messages = [
          "Your DataSnap already has the new firmware written.",
          "Rebooting to switch over to it now.",
          "Do not place on charger - the update will stop if you do.",
          "Do not close this window."
        ];
        this.syntheticProgress = 0;

        return await this.rebootAfterConfirm(stagedImage.version, stagedImage.hash);
      }

      // The "active slot unconfirmed" branch below fires cmdReset to roll back
      // to a known-good state. That's the RIGHT move on a truly fresh
      // unconfirmed boot, but WRONG when we've already detected a stuck-swap
      // (unconfirmed-active + target-in-non-active-slot is the classic stuck-
      // swap signature). Skip the rollback in that case and drive straight
      // into the upload — the upload itself will re-write the secondary slot
      // and put the device back into a consistent state.
      if(!activeImage.confirmed && !stagedImage && !FORCE_UPLOAD_FOR_CONFIRM_TEST) {
        console.log('[FWUpdate] active image unconfirmed - rolling back');
        this.rememberedDeviceName = this.device?.deviceName;
        this.primary_message = "Restarting DataSnap...";
        this.secondary_messages = [
          'Keep DataSnap close by',
          'Do not place on charger',
          'Do not close this window'
        ];
        if(await this.stoppedForCharger()) throw new Error(CHARGER_STOP_MESSAGE);
        this.startRebootProgressCrawl();
        this.cdr.detectChanges();

        await this.sendResetCommand();
        const disconnectAt = await this.waitForDisconnectEvent(15000);
        if(disconnectAt !== null) {
          this.primary_message = "Reconnecting to DataSnap...";
          this.cdr.detectChanges();
          await this.waitForReconnectEvent();
        }

        // The rollback itself is a recovery step, not a failure. MCUboot's revert may
        // have landed on a slot that is already the target version and confirmed (e.g.
        // a prior attempt's swap had already taken, and this "unconfirmed active" was
        // just the one-shot test boot reverting back to it) - re-read real post-reset
        // state instead of unconditionally declaring failure here.
        this.primary_message = "Verifying Update...";
        this.cdr.detectChanges();
        const postResetImageState = await this.mcumgrImageStateWithRetry().catch(() => undefined);
        const postResetActive = postResetImageState?.images.find(i => i.active) ?? postResetImageState?.images[0];
        const postResetVersion = this.parseVersion(postResetActive?.version);
        if(postResetActive?.confirmed && postResetVersion && !(await this.rwUpdateServerService.isFWOutOfDate(postResetVersion))) {
          console.log('[FWUpdate] rollback landed on an already-current confirmed image - treating as success');
          await this.finishProgressToComplete();
          this.primary_message = "Update Complete - Success!";
          this.secondary_messages = ["Your DataSnap is now up to date!"];
          this.update_complete = true;
          if(this.device) {
            this.device.isFWOutOfDate = false;
            this.device.updateFailed = false;
            this.device.updateChecked = true;
            this.device.currentlyUpdating = false;
          }
          this.device?.checkFWUpdate().catch(() => undefined);
          return 'success';
        }

        console.log('[FWUpdate] rollback complete but device is not yet on a confirmed current image - proceeding to upload');
        this.stopSyntheticProgressRamp();
      }
    } else {
      console.error("No images found for firmware state");
      throw new Error("No images found for firmware state");
    }

    const firmwareBlob = await this.rwUpdateServerService.getLatestFirmwareImage();
    const firmwareImage = await firmwareBlob.arrayBuffer();
    this.otaPhase = 'uploading';
    this.startUploadProgressAnimation();
    await this.device?.mcumgr?.cmdUpload(firmwareImage)
    console.log('[FWUpdate] upload done');

    let newFwHash: ArrayBuffer | null = null;
    let newFwVersion: string | undefined;

    imageState = await this.device?.mcumgr?.cmdImageState();
    if((imageState?.images.length??0) > 1) {
      newFwHash = imageState!.images[1].hash;
      newFwVersion = imageState!.images[1].version;
      // Test-arm (pending, not permanent) before reboot — if the new image fails to boot
      // cleanly, MCUboot auto-reverts since nothing is marked permanent yet. Made permanent
      // only after reconnect proves the device actually booted it (verifyAndConfirmActiveVersion).
      await this.testImageBeforeReboot(newFwHash, 'cmdImageTest (uploaded image)');
    } else {
      console.error("No images found for firmware state");
      throw new Error("No images found for firmware state");
    }

    this.displayedUploadProgress = 100;
    this.stopUploadProgressAnimation();

    return await this.rebootAfterConfirm(newFwVersion, newFwHash ?? undefined);
  }

  private handleUpdateFailure(error: unknown) {
    if(((error as any)?.message ?? String(error)) === CHARGER_STOP_MESSAGE) {
      // Deliberate stop, not a failure: the service already disconnected the sensor and showed
      // "Update Stopped". No retry state/error count - the user reconnects off the charger.
      console.warn('[FWUpdate] update stopped: device placed on charger');
      this.otaPhase = 'idle';
      this.primary_message = "Update Stopped";
      this.secondary_messages = ["The sensor was placed on the charger. Remove it from the charger and reconnect it to try again."];
      return;
    }
    console.error('Firmware update failed:', error);
    this.otaPhase = 'failed';
    this.errorCount++
    this.primary_message = "Update Failed";
    this.update_failed = true;
    // FAILED is terminal for this attempt — let keepalive/housekeeping resume now rather than
    // waiting for the user to tap Retry/Cancel; retryUpdate() flips it back true if they retry.
    if(this.device) {
      this.device.currentlyUpdating = false;
      // A failure thrown mid-reset/reboot (before verify ever clears these — see
      // rebootAfterConfirm()/tapToFinishReconnect()) would otherwise leave rebootInProgress/
      // otaVerifying stuck true forever, silently suppressing the mid-upload stuck-reconnect
      // recovery on every future attempt with this device instance.
      this.device.rebootInProgress = false;
      this.device.otaVerifying = false;
    }
    const reason = (error as any)?.message ?? String(error);
    if(this.errorCount >=3) {
      this.secondary_messages = [
        reason,
        "Ensure DataSnap is kept close to this device and is fully charged.",
        "Please try to update this DataSnap later when the device is fully charged and there is less interference."
      ]
    } else {
      this.secondary_messages = [
        reason,
        "There was an issue updating this DataSnap",
        "Do not place DataSnap on charger while updating."
      ]
    }
  }

  startUpdate = async () => {
    // Build marker — if you do NOT see this exact line in the console, the browser is
    // running a STALE bundle. Hard-reload (Ctrl+Shift+R) after redeploying.
    console.log('[FWUpdate] >>> build marker: OTA state machine (RESETTING -> REBOOTING[disconnect] -> RECONNECTING -> VERIFYING -> SUCCESS) <<<');
    // Item 5: one-shot environment snapshot. getDevices===undefined disables every
    // address-rotation recovery path (raw gatt-probe + BleClient handle refresh), so capture it
    // alongside the UA/Chrome version/secure-context that gate Web Bluetooth availability.
    if (OTA_DIAG) {
      const bt = (typeof navigator !== 'undefined') ? (navigator as any).bluetooth : undefined;
      const ua = (typeof navigator !== 'undefined') ? navigator.userAgent : '?';
      const chromeVer = /Chrom(?:e|ium)\/([\d.]+)/.exec(ua)?.[1] ?? 'n/a';
      this.diag(`OTA start env: getDevices=${typeof bt?.getDevices}, isSecureContext=${typeof isSecureContext !== 'undefined' ? isSecureContext : '?'}, chrome=${chromeVer}, ua="${ua}"`);
    }
    this.device!.currentlyUpdating = true;
    // Bug 20264: never start (or re-attach to) an OTA on a device whose last-polled state is
    // charging — abort and disconnect before a single mcumgr command is sent.
    if(isChargingState(this.device!.batteryState)) {
      this.datasnapDeviceService.onChargerDetected(this.device!);
      return;
    }
    if(this.errorCount >= 3) {
      console.error("Firmware update failed after 3 attempts");
      return;
    }
    this.reconnecting = false;
    this.showManualReconnect = false;
    this.update_complete = false;
    this.update_failed = false;
    this.update_ambiguous = false;
    this.update_tap_required = false;
    this.tapToFinishBusy = false;
    this.tapToFinishMessage = null;
    this.update_reconnect_stuck = false;
    this.otaReconnectBusy = false;
    this.otaReconnectMessage = null;
    this.update_reconnect_ready = false;

    // Re-entry guard: the firmware-update route is non-reusable, so a reconnect/
    // navigation mid-update tears this component down and builds a brand-new one that
    // calls startUpdate() again. If an OTA state machine is already running on this
    // device (started by the instance we're replacing), attach to that SAME run instead
    // of starting a second, independent one — this is what keeps "success" tied to one
    // real reset -> disconnect -> reconnect -> verify sequence no matter how many
    // component instances the route cycles through while it's in flight.
    if(this.device?.fwUpdateInFlight) {
      console.log('[FWUpdate] re-entry detected - attaching to in-flight OTA state machine instead of restarting');
      this.otaPhase = 'verifying';
      this.primary_message = "Applying Firmware Update - Please Wait";
      this.secondary_messages = [
        "Your DataSnap is finishing its firmware update.",
        "Do not close this window."
      ];
      this.startRebootProgressCrawl();
      try {
        const result = await this.device.fwUpdateInFlight;
        if(result === 'installed') {
          // Mirrors rebootAfterConfirm()'s own ambiguous outcome for this instance — the
          // owning instance already declared this; this is just this instance's own view.
          this.otaPhase = 'installed';
          this.primary_message = "Update installed. Your DataSnap is restarting - reconnect it from the device list to confirm.";
          this.secondary_messages = [];
          this.update_ambiguous = true;
        } else if(result === 'manualSelectRequired') {
          // Mirrors rebootAfterConfirm()'s own manual-select outcome for this instance — the
          // owning instance already declared this; show this instance's own tap-to-finish button.
          this.otaPhase = 'manualSelectRequired';
          this.primary_message = "Your DataSnap restarted. Tap to finish.";
          this.secondary_messages = [];
          this.update_tap_required = true;
        } else {
          this.otaPhase = 'success';
          this.primary_message = "Update Complete - Success!";
          this.secondary_messages = ["Your DataSnap is now up to date!"];
          this.update_complete = true;
          if(this.device) {
            this.device.currentlyUpdating = false;
          }
        }
      } catch (error) {
        this.handleUpdateFailure(error);
      } finally {
        this.stopSyntheticProgressRamp();
        this.cdr.detectChanges();
      }
      return;
    }

    // Defensive: guarantees a clean slate even if a prior attempt threw mid-reset/reboot and
    // left these stuck true (handleUpdateFailure() now also clears them at failure time — this
    // covers any other path that doesn't go through it). Placed after the re-entry guard above
    // (and its `return`) so an instance merely attaching to another's in-flight OTA never clears
    // flags that the owning instance's live reset/reboot/verify run still depends on.
    this.device!.rebootInProgress = false;
    this.device!.otaVerifying = false;

    this.otaPhase = 'idle';
    this.syntheticProgress = null;
    this.stopSyntheticProgressRamp();

    const runPromise = this.runUpdateFlow();
    this.device!.fwUpdateInFlight = runPromise;
    try {
      await runPromise;
    } catch (error) {
      if(error instanceof ReconnectToVerifyError) {
        // UI is already in the reconnect-to-verify state — not a success, not a failure.
        console.log('[FWUpdate] update applied but device not back yet — awaiting reconnect to verify');
      } else {
        this.handleUpdateFailure(error);
      }
    } finally {
      // Only the instance that actually started this run clears it — an attached
      // re-entrant instance (above) must never clear a run it didn't own.
      if(this.device?.fwUpdateInFlight === runPromise) {
        this.device.fwUpdateInFlight = null;
      }
      this.stopUploadProgressAnimation();
      // Every exit path above (success, already-up-to-date, failure) flips view-bound
      // flags (update_complete/update_failed/messages) from inside async continuations
      // resumed by BLE/GATT events — force a render here so the final state always
      // reaches the screen.
      this.cdr.detectChanges();
    }
  }

  private parseVersion(version: string | undefined): {
    versionMajor: number;
    versionMinor: number;
    versionBug: number;
    versionSpecial: number;
  } | null {
    if(!version) return null;
    // Accept both MCUboot "0.5.4+0" and Zephyr img_mgmt "0.5.4.0" formats, plus
    // a leading "v", surrounding whitespace, and trailing pre-release tags.
    const match = version.trim().replace(/^v/i, '').match(/^(\d+)\.(\d+)\.(\d+)(?:[.+](\d+))?/);
    if(!match) return null;
    return {
      versionMajor: Number(match[1]),
      versionMinor: Number(match[2]),
      versionBug: Number(match[3]),
      versionSpecial: match[4] ? Number(match[4]) : 0
    };
  }

  bufferToHex(buffer: ArrayBuffer | undefined): string {
    if(!buffer) return '';
    return Array.from(new Uint8Array(buffer)).map(b => b.toString(16).padStart(2, '0')).join('');
   }

   retryUpdate = () => {
    this.update_failed = false;
    this.update_reconnect_ready = false;
    this.startUpdate();
   }

   cancelUpdate = () => {
    // Dismiss the failure popup without retrying. Retry re-runs the pre-check
    // that fires cmdReset() when the primary slot is unconfirmed, which caused
    // an unwanted reboot on plain dismissal (19546).
    this.backToLessonFailed();
   }

  backToLesson = () => {
    this.router.navigate(['home', this.state.unitId, this.state.levelId, this.state.lessonId]);
    this.device!.currentlyUpdating = false;
  }

  backToLessonFailed = () => {
    if(this.device) {
      this.device.updateFailed = true;
      this.device.currentlyUpdating = false;
    }
    this.backToLesson();
  }

  openProductSupport = () => {
    window.open('https://www.realityworks.com/support/', '_blank');
  }

  showSelectDevicePopup = (): boolean => {
    // waitForReconnectEvent() owns reconnect messaging for the duration of an OTA
    // (its own "reconnecting"/showManualReconnect flags below the progress bar) —
    // the generic mid-lesson "Reconnect Device" popup is for ordinary disconnects
    // and must never surface over an active update, even if the BLE layer's own
    // updateInProgress gating is ever raced by a disconnect/reconnect event.
    return (this.device?.hasDevice
      && this.device?.needsReselect
      && !this.device?.currentlyUpdating) ?? false;
  }
  reselectDevice = () => {
    if(!this.device || this.reselecting) return;
    this.reselecting = true;
    console.log('[FWUpdate] state=RECONNECTING: manual reconnect clicked');
    this.datasnapDeviceService.reselectDevice(this.device)
      .then(() => this.waitForManualReconnectOutcome())
      .finally(() => {
        this.reselecting = false;
        this.cdr.detectChanges();
      });
  }

  // datasnapDeviceService.reselectDevice() resolves as soon as the new device's connect()
  // call is merely invoked, not once it actually succeeds or fails - the real GATT handshake
  // (BleClient.connect()) runs asynchronously in the background after that. Without this
  // wait, "Connecting..." reverted to "Reconnect" within milliseconds of every click
  // regardless of outcome, making a reconnect that was still failing in the background look
  // like a broken "Connecting -> Reconnect" loop. waitForReconnectEvent() (already running
  // from the rollback-recovery path) still owns clearing showManualReconnect/driving verify
  // on a real success; this only keeps the button's own disabled state honest while that's
  // pending.
  private waitForManualReconnectOutcome(timeoutMs = 22000): Promise<void> {
    return new Promise((resolve) => {
      const subject = this.device?.deviceStateChangeSubject;
      if(!subject) { resolve(); return; }
      let settled = false;
      const sub = subject.subscribe((state) => {
        if(settled || state !== Device_State.READY_FOR_PACKETS) return;
        settled = true;
        clearTimeout(timer);
        sub.unsubscribe();
        console.log('[FWUpdate] state=RECONNECTING: manual reconnect succeeded');
        resolve();
      });
      const timer = setTimeout(() => {
        if(settled) return;
        settled = true;
        sub.unsubscribe();
        console.warn('[FWUpdate] state=RECONNECTING: manual reconnect attempt timed out - button re-enabled for retry');
        resolve();
      }, timeoutMs);
    });
  }

}
